import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { useKits } from "@/hooks/useKits";
import { useMessages } from "@/hooks/useMessages";
import { useAlerts } from "@/hooks/useAlerts";
import { useKitImages } from "@/hooks/useKitImages";
import { useKitLogo } from "@/hooks/useKitLogo";
import { useKitMarket } from "@/hooks/useKitMarket";
import { TSPEvent } from "@/types/tsp";
import { TickerContent } from "@/components/ticker/TickerContent";
import { LogoSection } from "@/components/ticker/LogoSection";
import { AlertSection } from "@/components/ticker/AlertSection";
import { STORAGE_URL } from "@/lib/supabase";
import { GameContent } from "@/components/ticker/GameContent";
import "../components/ticker/ticker-styles.css";

export default function FullScreenTicker() {
  const { number } = useParams();
  const { getKitByNumber } = useKits();
  
  // First get the kit by its number to get the UUID
  const { data: kit, isLoading: isKitLoading } = useQuery({
    queryKey: ['kit-by-number', number],
    queryFn: () => getKitByNumber(number || ''),
    enabled: !!number,
  });

  const kitId = kit?.id;

  // Only fetch related data once we have the kit's UUID
  const { messages } = useMessages(kitId);
  const { alerts } = useAlerts(kitId);
  const { kitImageConfigs } = useKitImages(kitId);
  const { kitLogo } = useKitLogo(kitId);
  const { kitMarket } = useKitMarket(kitId);

  // Fetch upcoming games
  const { data: upcomingGames = [] } = useQuery<TSPEvent[]>({
    queryKey: ['tsp-events'],
    queryFn: async () => {
      const response = await fetch('https://gotsp.live/wp-json/wp/v2/events?status=publish&per_page=100');
      if (!response.ok) {
        throw new Error('Failed to fetch events');
      }
      return response.json();
    },
    refetchInterval: 5 * 60 * 1000,
    staleTime: 4 * 60 * 1000,
  });

  useEffect(() => {
    // Hide cursor in fullscreen mode
    document.body.style.cursor = 'none';
    
    // Set background color to black for the entire page
    document.body.style.backgroundColor = 'black';
    
    return () => {
      document.body.style.cursor = 'auto';
      document.body.style.backgroundColor = '';
    };
  }, []);

  // Memoized values
  const activeMessages = React.useMemo(() => 
    messages
      .filter(msg => msg.is_active)
      .map(msg => msg.text),
    [messages]
  );

  const currentAlertMessage = React.useMemo(() => 
    alerts.find(alert => alert.is_active)?.text,
    [alerts]
  );

  const selectedMarket = React.useMemo(() => 
    kitMarket?.market_short_name || "SAT",
    [kitMarket]
  );

  const filteredGames = React.useMemo(() => 
    upcomingGames
      .filter(event => 
        event.meta.event_status === "Live" || event.meta.event_status === "Scheduled"
      )
      .sort((a, b) => {
        if (a.meta.event_status === "Live" && b.meta.event_status !== "Live") return -1;
        if (b.meta.event_status === "Live" && a.meta.event_status !== "Live") return 1;
        const dateA = new Date(`${a.meta.event_date} ${a.meta.event_time}`);
        const dateB = new Date(`${b.meta.event_date} ${b.meta.event_time}`);
        return dateA.getTime() - dateB.getTime();
      })
      .slice(0, 5),
    [upcomingGames]
  );

  const [currentGameIndex, setCurrentGameIndex] = React.useState(0);
  const [isTransitioning, setIsTransitioning] = React.useState(false);
  const [prevGameIndex, setPrevGameIndex] = React.useState(0);

  React.useEffect(() => {
    if (filteredGames.length <= 1) return;

    const timer = setInterval(() => {
      setPrevGameIndex(currentGameIndex);
      setIsTransitioning(true);
      
      setTimeout(() => {
        setCurrentGameIndex(prev => (prev + 1) % filteredGames.length);
        setIsTransitioning(false);
      }, 300);
    }, 13000);

    return () => clearInterval(timer);
  }, [filteredGames.length, currentGameIndex]);

  if (isKitLoading) {
    return null; // No loading state in fullscreen view
  }

  if (!kit) {
    return null; // No error state in fullscreen view
  }

  const currentGameDisplay = filteredGames[currentGameIndex];
  const prevGameDisplay = filteredGames[prevGameIndex];

  return (
    <div className="fixed inset-0 bg-transparent overflow-hidden flex items-end">
      <div className="w-full" style={{ height: 'calc((100vw*69)/1920)' }}>
        <div className="h-full flex">
          <LogoSection logo={kitLogo} />

          <div 
            className="w-[1220px] h-full overflow-hidden shrink-0 flex flex-col"
            style={{ backgroundColor: kit.background_color }}
          >
            <div className="h-[31px] flex pt-[2px]">
              <div className="game-container flex-1 pl-8">
                {prevGameDisplay && isTransitioning && (
                  <GameContent 
                    game={prevGameDisplay} 
                    className="exit"
                    textColor={kit.text_color}
                  />
                )}
                {currentGameDisplay && (
                  <GameContent 
                    game={currentGameDisplay}
                    className={isTransitioning ? 'enter' : 'active'}
                    textColor={kit.text_color}
                  />
                )}
              </div>
              <div 
                className="flex items-center px-8 border-l h-[22px] my-auto shrink-0"
                style={{ borderColor: `${kit.text_color}80`, borderLeftWidth: '2px' }}
              >
                <div className="text-[16px] font-[Helvetica] whitespace-nowrap" style={{ color: kit.text_color }}>
                  {selectedMarket}
                  {kitMarket?.weather_temp && ` ${kitMarket.weather_temp}°F`}
                  {kitMarket?.weather_condition && ` ${kitMarket.weather_condition}`}
                  {format(new Date(), ' h:mm:ss a')} CST
                </div>
              </div>
            </div>

            <div className="h-[38px] pt-2">
              <TickerContent messages={activeMessages} textColor={kit.text_color} speed={kit.ticker_speed || 20} />
            </div>
          </div>

          <AlertSection 
            displayImage={kitImageConfigs.find(img => img.is_active)}
            showAlertMessage={!!currentAlertMessage}
            alertMessage={currentAlertMessage}
            aspectRatio={350/80}
          />
        </div>
      </div>
    </div>
  );
}
